<template>
    <div class="modal-content" ref="container">
        <form>
            <div class="modal-header clearfix">
                <h3 class="modal-title pull-left">Export {{ code }}</h3>
                <button type="button" aria-label="Close" class="close icon icon-close" @click="close()" />
            </div>
            <div class="modal-body">
                <!--
                <div describedby-text class="alert alert-danger">
                    This will *REPLACE* all existing tenant settings, address book data, homepage data, pages definitions, etc.
                </div>
                -->
                <div class="clearfix">
                    <input type="hidden" name="dummy" value="dummy" />
                    <div class="form-group">
                        <div class="form-check">
                            <input type="checkbox" class="form-check-input" name="keepOpen" id="keepOpen" v-model="keepOpen" />
                            <label class="form-check-label" for="keepOpen">Keep Open</label>
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="form-check">
                            <input type="checkbox" class="form-check-input" name="includeSubmissions" id="includeSubmissions" v-model="includeSubmissions" />
                            <label class="form-check-label" for="includeSubmissions">Include Submissions</label>
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="form-check">
                            <input type="checkbox" class="form-check-input" name="includeSubtenants" id="includeSubtenants" v-model="includeSubtenants" />
                            <label class="form-check-label" for="includeSubtenants">Include Sub-tenants</label>
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="form-check">
                            <input type="checkbox" class="form-check-input" name="includePrepublishDrafts" id="includePrepublishDrafts" v-model="includePrepublishDrafts" />
                            <label class="form-check-label" for="includePrepublishDrafts">Include Pre-publish Drafts</label>
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="form-check">
                            <input type="checkbox" class="form-check-input" name="onlyIncludeSubmissions" id="onlyIncludeSubmissions" v-model="onlyIncludeSubmissions" />
                            <label class="form-check-label" for="onlyIncludeSubmissions"><strong>Only</strong> Include Submissions</label>
                        </div>
                    </div>
                    <div class="form-group">
                        <div class="form-check-inline">
                            <input type="radio" class="form-check-input" name="filterSubmissionsByLastUpdated" id="filterSubmissionsByLastUpdated" v-model="filterSubmissionsByLastUpdated" :value="true" />
                            <label class="form-check-label" for="filterSubmissionsByLastUpdated">Filter Submissions by Last Updated</label>
                        </div>
                        <div class="form-check-inline">
                            <input type="radio" class="form-check-input" name="filterSubmissionsByLastUpdated" id="filterSubmissionsByLastUpdated" v-model="filterSubmissionsByLastUpdated" :value="false" />
                            <label class="form-check-label" for="filterSubmissionsByLastUpdated">Filter Submissions by Submitted Date</label>
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="startSubmissionFilter">Only Include Submissions After:</label>
                        <DatePicker :config="datepickerConfig" name="startSubmissionFilter" id="startSubmissionFilter" v-model="startSubmissionFilter" />
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="endSubmissionFilter">Only Include Submissions Before:</label>
                        <DatePicker :config="datepickerConfig" name="endSubmissionFilter" id="endSubmissionFilter" v-model="endSubmissionFilter" />
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="submissionPeriod">Export Submission X Days at a Time:</label>
                        <input type="number" :class="['form-control']" name="submissionPeriod" id="submissionPeriod" v-model="submissionPeriod" />
                    </div>
                    <div describedby-text class="alert alert-warning m-0">Sometimes the download is blocked by the popup blocker. If you don't see a download, ensure you allow this popup.</div>
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-primary" id="exportBtn" @click="exportFunc">
                    Export
                    <AsyncOperationStatus :promise="exportPromise" />
                </button>
                <button type="button" class="btn btn-bare" @click="close()">Cancel</button>
            </div>
        </form>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, SetupContext, inject } from "vue";
import { fetchXsrf, throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import DatePicker from "@/lib/DatePicker.3.vue";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { useMainStore } from "@/mainStore";
import { changePageInjectKey } from "@/AppShell.3.vue.InjectKeys";
import { TenantExportResults } from "@cs-ts/tenant-export-results";
type Props = { code: string } & DialogProps;

export default defineComponent({
    components: {
        DatePicker,
        AsyncOperationStatus,
    },
    props: ["code", "triggerElement"],
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        const dialogMixin = dialogSetup(containerRef, props);
        const mainStore = useMainStore();

        const includeSubmissions = ref(true);
        const includeSubtenants = ref(true);
        const includePrepublishDrafts = ref(true);
        const onlyIncludeSubmissions = ref(false);
        const filterSubmissionsByLastUpdated = ref(true);
        const startSubmissionFilter = ref<string>(null!);
        const endSubmissionFilter = ref<string>(null!);
        const submissionPeriod = ref<string>(null!);
        // const period = ref(null);
        const keepOpen = ref(false);
        const exportPromise = ref<Promise<void>>(null!);

        const datepickerConfig = computed(() => {
            return {
                allowInput: true,
                altInput: true,
                dateFormat: "m-d-Y",
                altFormat: "m-d-Y",
                disableMobile: true,
                defaultDate: "",
                wrap: true,
                clickOpens: false,
                onOpen: () => {
                    dialogMixin.focusTrapSetEnabled(false);
                },
                onClose: () => {
                    dialogMixin.focusTrapSetEnabled(true);
                },
            };
        });

        const close = () => {
            emit("close");
        };
        const exportFunc = async () => {
            const doExport = async (startSubmissionFilter: string, endSubmissionFilter: string) => {
                const exportUrl = await mainStore.getUrl("adminTenantManagementExport", {
                    code: props.code,
                    includeSubtenants: includeSubtenants.value,
                    includeSubmissions: includeSubmissions.value,
                    includePrepublishDrafts: includePrepublishDrafts.value,
                    onlyIncludeSubmissions: onlyIncludeSubmissions.value,
                    filterSubmissionsByLastUpdated: filterSubmissionsByLastUpdated.value,
                    startSubmissionFilter: startSubmissionFilter,
                    endSubmissionFilter: endSubmissionFilter,
                });
                const fetchResult = await fetchXsrf(mainStore, exportUrl);
                throwIfNonSuccessStatus(fetchResult);
                const jsonResult: TenantExportResults = await fetchResult.json();
                if (jsonResult.success) {
                    const downloadUrl = await mainStore.getUrl("adminTenantManagementExportDownload", {
                        code: props.code,
                        token: jsonResult.token,
                        type: "zip",
                    });
                    const downloadWindow = window.open(downloadUrl);
                    await new Promise<void>((resolve, reject) => {
                        const f = () => {
                            try {
                                if (downloadWindow!.closed) {
                                    resolve();
                                } else {
                                    setTimeout(f, 100);
                                }
                            } catch (e) {
                                reject(e);
                            }
                        };
                        f();
                    });
                } else {
                    throw new Error(JSON.stringify(jsonResult));
                }
            };
            let _startSubmissionFilter = startSubmissionFilter.value;
            let _endSubmissionFilter = endSubmissionFilter.value;
            if (submissionPeriod.value != null && !/^\s*$/.test(submissionPeriod.value)) {
                var period = parseInt(submissionPeriod.value);
                if (period >= 1) {
                    const moment = (await import(/* webpackChunkName: "Moment", webpackPrefetch: true */ "moment")).default;
                    let startDateTime = moment(_startSubmissionFilter);
                    let middleDateTime = startDateTime.clone().add(period, "days");
                    const endDateTime = moment(_endSubmissionFilter);
                    while (middleDateTime.isBefore(endDateTime)) {
                        await (exportPromise.value = doExport(startDateTime.toISOString(), middleDateTime.toISOString()));
                        startDateTime = middleDateTime;
                        middleDateTime = startDateTime.clone().add(period, "days");
                    }
                    await (exportPromise.value = doExport(startDateTime.toISOString(), endDateTime.toISOString()));
                } else {
                    debugger;
                }
            } else {
                try {
                    await (exportPromise.value = doExport(_startSubmissionFilter, _endSubmissionFilter));
                } catch {
                    return;
                }
            }
            if (!keepOpen.value) {
                close();
            }
        };

        return {
            // Refs
            container: containerRef,

            // States
            includeSubmissions,
            includeSubtenants,
            includePrepublishDrafts,
            onlyIncludeSubmissions,
            filterSubmissionsByLastUpdated,
            startSubmissionFilter,
            endSubmissionFilter,
            submissionPeriod,
            keepOpen,
            exportPromise,

            // Computed
            datepickerConfig,

            // Methods
            close,
            exportFunc,

            changePage: inject(changePageInjectKey)!,
        };
    },
});
</script>
